import React from "react";
import {
  Autocomplete,
  CircularProgress,
  TextField,
  useTheme,
} from "@mui/material";

function InputValue({ name, multiple, value }) {
  if (name == null) {
    return null;
  }

  if (multiple) {
    return value.map((o) => (
      <input key={o.id} type="hidden" name={name + "[]"} value={o.id ?? ""} />
    ));
  }

  return <input type="hidden" name={name} value={value?.id ?? ""} />;
}

/**
 * @typedef AutocompleteSelectOption
 * @property {string} id
 * @property {string} name
 *
 * @param {object} props
 * @param {AutocompleteSelectOption[]} props.options
 * @param {boolean} [props.loading]
 * @param {string} props.label
 * @param {(option: AutocompleteSelectOption) => void} [props.onChange]
 * @param {string} [props.name]
 * @param {AutocompleteSelectOption} [props.value]
 * @param {AutocompleteSelectOption} [props.defaultValue]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.readOnly]
 * @param {boolean} [props.required]
 * @param {boolean} [props.multiple]
 * @param {string} [props.size]
 * @param {boolean} [props.autoFocus]
 * @param {Function} [props.groupBy]
 * @param {boolean} [props.disableClearable]
 * @param {string} [props.warningText] // optional warning text shown next to label in orange
 */
export default function AutocompleteSelect(props) {
  const theme = useTheme();

  const {
    options,
    loading,
    label,
    onChange,
    name,
    disabled,
    value: externalValue,
    readOnly,
    required: externalRequired,
    defaultValue,
    multiple,
    size,
    autoFocus,
    groupBy,
    disableClearable,
    warningText,
    onFocus,
    onBlur,
  } = props;
  const [internalValue, setInternalValue] = React.useState(
    defaultValue ?? (multiple ? [] : null),
  );
  const value = externalValue === undefined ? internalValue : externalValue;
  const required =
    multiple && externalRequired ? value.length === 0 : externalRequired;

  return (
    <>
      <InputValue name={name} value={value} multiple={multiple} />
      <Autocomplete
        size={size}
        multiple={multiple}
        autoHighlight={true}
        disableCloseOnSelect={multiple}
        disabled={disabled}
        value={externalValue ?? value}
        loading={loading}
        options={options}
        getOptionLabel={(opt) => opt.name}
        getOptionKey={(opt) => opt.id}
        readOnly={readOnly}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        renderInput={(params) => (
          <TextField
            label={
              warningText ? (
                <span>
                  {label}
                  {" - "}
                  <span style={{ color: theme.palette.warning.dark }}>
                    {warningText}
                  </span>
                </span>
              ) : (
                label
              )
            }
            autoFocus={autoFocus}
            required={required}
            {...params}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        onChange={(_, value) => {
          setInternalValue(value);
          onChange?.(value);
        }}
        groupBy={groupBy}
        disableClearable={disableClearable}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    </>
  );
}
